import React, { useCallback, useEffect } from 'react'
import { projectStatus } from '../utils/status'
import { uniqueSlug } from '../utils/links'
import { supabaseConfigured } from '../lib/supabase'
import { fetchProjects } from '../utils/projectsSource'
import { fetchShows } from '../utils/showsSource'
import { assignProjectShow, insertShow, updateShowRow } from '../utils/showWrites'
import { deleteProjectRow, insertProject, updateProjectRow } from '../utils/projectWrites'
import {
  assignProjectMember,
  removeProjectMember,
  updateProjectMemberRole,
} from '../utils/projectTeam'
import type { StaffSession } from '../utils/staffAuth'
import type { Project, ProjectTemplate, Show } from '../types/portal'
import { byEpisodeName, fromTemplate, stamp, uid } from './portalHelpers'
import type { ClientRoute, PortalRoute, PortalState } from './portalTypes'

/**
 * The project level of the Portal provider: reloading projects and shows from
 * the database, and every project/show/team action on the context value.
 *
 * `usePortalProjects` is a real hook, called by `PortalProvider` at exactly
 * the point in its hook sequence where this code used to sit inline, so React
 * sees the same hooks in the same order with the same dependency arrays.
 *
 * `buildProjectActions` is deliberately NOT a hook: it is called inside the
 * provider's existing `useMemo`, so the context value keeps its single memo
 * boundary and the same dependency array it always had.
 */

interface PortalProjectsInput {
  setProjects: React.Dispatch<React.SetStateAction<Project[]>>
  setShows: React.Dispatch<React.SetStateAction<Show[]>>
  staffSession: StaffSession | null
  authed: boolean
}

export function usePortalProjects({
  setProjects,
  setShows,
  staffSession,
  authed,
}: PortalProjectsInput) {
  /**
   * Projects and deliverables come from the database — there is no seeded
   * client data. Also called straight after a project is created so the stored
   * rows, not the local copy, are what the studio sees.
   */
  const reloadProjects = useCallback(async () => {
    if (!supabaseConfigured) return
    const rows = await fetchProjects()
    if (rows.length === 0) return
    setProjects((current) => {
        /* Anything created in this session and not yet in the database stays. */
        const local = current.filter((item) => !rows.some((row) => row.slug === item.slug))
        const merged = rows.map((row) => {
          const held = current.find((item) => item.slug === row.slug)
          if (!held) return row
          /* Stored columns win; fields the schema has no column for (notes,
             requests, producer) stay as held. Type, description and client
             contact now have real columns too (a project's own, or mirrored
             from its show) — held only covers a value not yet round-tripped
             through a save, so an empty held value falls back to what was
             actually fetched rather than hiding it. */
          return {
            ...held,
            ...row,
            id: row.id,
            type: held.type || row.type,
            description: held.description || row.description,
            nextShootDate: held.nextShootDate,
            nextShootTime: held.nextShootTime,
            location: held.location,
            clientContact: held.clientContact || row.clientContact,
            producer: held.producer,
            callSheet: held.callSheet,
            shoots: held.shoots,
            team: held.team,
            deliverables: row.deliverables.map((item) => {
              const local = held.deliverables.find((entry) => entry.id === item.id)
              if (!local) return item
              return {
                ...local,
                name: item.name,
                type: item.type,
                status: item.status,
                version: item.version,
                visibleToClient: item.visibleToClient,
                /* Stored review links and framing are authoritative, so a
                   reload shows exactly what was saved. */
                driveUrl: item.driveUrl,
                audioUrl: item.audioUrl,
                videoFormat: item.videoFormat,
                /* Stored version history, and the current version's real
                   publication date where one was recorded. */
                versions: item.versions,
                versionDate: item.versionDate !== '—' ? item.versionDate : local.versionDate,
                /* Stored transcript text and its saved states, so both sides
                   read the same document. */
                transcript: item.transcript ?? local.transcript,
                transcriptRevisions: item.transcriptRevisions ?? local.transcriptRevisions,
                /* The stored source document and its length. */
                transcriptFilename: item.transcriptFilename ?? local.transcriptFilename,
                transcriptWordCount: item.transcriptWordCount ?? local.transcriptWordCount,
              }
            }),
          }
        })
        return [...merged, ...local]
    })
  }, [])

  const reloadShows = useCallback(async () => {
    if (!supabaseConfigured) return
    const rows = await fetchShows()
    if (rows.length === 0) return
    setShows(rows)
  }, [])

  useEffect(() => {
    void reloadProjects()
    void reloadShows()
  }, [staffSession, authed, reloadProjects, reloadShows])

  return { reloadProjects, reloadShows }
}

interface ProjectActionInput {
  projects: Project[]
  projectId: string
  activeProject: Project
  templates: ProjectTemplate[]
  shows: Show[]
  activeShow: Show | null
  activeShowId: string
  activeAdminShow: Show | null
  activeAdminShowId: string
  showProjectsForClient: Project[]
  setProjects: React.Dispatch<React.SetStateAction<Project[]>>
  setTemplates: React.Dispatch<React.SetStateAction<ProjectTemplate[]>>
  setShows: React.Dispatch<React.SetStateAction<Show[]>>
  setActiveProjectId: React.Dispatch<React.SetStateAction<string>>
  setActiveDeliverableId: React.Dispatch<React.SetStateAction<string>>
  setFocusDeliverableId: React.Dispatch<React.SetStateAction<string>>
  setActiveAdminShowId: React.Dispatch<React.SetStateAction<string>>
  setPortalRoute: React.Dispatch<React.SetStateAction<PortalRoute>>
  setClientRoute: React.Dispatch<React.SetStateAction<ClientRoute>>
  patchProject: (projectId: string, patch: Partial<Project>) => void
  notify: (message: string) => void
  reloadProjects: () => Promise<void>
  reloadShows: () => Promise<void>
}

export type ProjectActions = Pick<
  PortalState,
  | 'projects'
  | 'activeProjects'
  | 'archivedProjects'
  | 'activeProject'
  | 'openProject'
  | 'templates'
  | 'shows'
  | 'activeShow'
  | 'showProjects'
  | 'openShowProject'
  | 'cameFromShow'
  | 'goBackToShow'
  | 'createShow'
  | 'setProjectShow'
  | 'addEpisode'
  | 'openShow'
  | 'activeAdminShow'
  | 'adminShowProjects'
  | 'addEpisodeToShow'
  | 'selectProject'
  | 'updateShow'
  | 'projectComplete'
  | 'archiveProject'
  | 'deleteProject'
  | 'findExistingProject'
  | 'reopenProject'
  | 'updateProject'
  | 'createProject'
  | 'duplicateProject'
  | 'saveAsTemplate'
  | 'updateShoot'
  | 'setProjectTeam'
  | 'addTeamMember'
  | 'updateTeamMember'
  | 'removeTeamMember'
>

export function buildProjectActions({
  projects,
  projectId,
  activeProject,
  templates,
  shows,
  activeShow,
  activeShowId,
  activeAdminShow,
  activeAdminShowId,
  showProjectsForClient,
  setProjects,
  setTemplates,
  setShows,
  setActiveProjectId,
  setActiveDeliverableId,
  setFocusDeliverableId,
  setActiveAdminShowId,
  setPortalRoute,
  setClientRoute,
  patchProject,
  notify,
  reloadProjects,
  reloadShows,
}: ProjectActionInput): ProjectActions {
  return {
    projects,
    activeProjects: projects.filter((item) => !item.archived),
    archivedProjects: projects.filter((item) => item.archived),
    activeProject,
    openProject: (id) => {
      setActiveProjectId(id)
      setFocusDeliverableId('')
      setPortalRoute('project')
    },
    templates,

    shows,
    activeShow,
    showProjects: showProjectsForClient,
    openShowProject: (id) => {
      setActiveProjectId(id)
      setFocusDeliverableId('')
      setClientRoute('home')
    },
    /* Only true when there is a real list to go back to — a one-episode
       show routes straight into its episode (see the bootstrap effect
       above) and never shows this link in the first place. */
    cameFromShow: Boolean(activeShowId) && showProjectsForClient.length > 1,
    goBackToShow: () => {
      if (!activeShowId) return
      setClientRoute('show')
    },
    createShow: ({ client, name }) => {
      const slug = uniqueSlug(
        client,
        name,
        shows.map((item) => item.slug),
      )
      void insertShow({ client, name, slug }).then(({ result }) => {
        if (!result.ok) {
          notify(`Not saved · ${result.error}`)
          return
        }
        void reloadShows()
      })
    },
    setProjectShow: (showId) => {
      const previous = activeProject.showId
      patchProject(projectId, { showId: showId || undefined })
      void assignProjectShow(projectId, showId).then((result) => {
        if (!result.ok) {
          patchProject(projectId, { showId: previous })
          notify(`Not saved · ${result.error}`)
        }
      })
    },
    addEpisode: (name) => {
      const episodeName = name.trim()
      if (!episodeName) return

      /* Inserts the new episode under the given show, exactly the way
         createProject does when a show is chosen up front. */
      const insertEpisode = (showId: string) => {
        const id = uid('proj')
        const created: Project = {
          id,
          slug: uniqueSlug(
            activeProject.client,
            episodeName,
            projects.map((item) => item.slug),
          ),
          /* Client, type, contact and description are shared across the
             show, so the new episode starts already in step with its
             siblings rather than blank. */
          client: activeProject.client,
          name: episodeName,
          type: activeProject.type,
          description: activeProject.description,
          status: 'Planned',
          nextShootDate: '',
          nextShootTime: '',
          location: '',
          clientContact: activeProject.clientContact,
          producer: 'Portal Productions',
          updated: stamp(),
          deliverables: [],
          shoots: [],
          team: [],
          archived: false,
        }
        setProjects((list) => [created, ...list])
        setActiveProjectId(id)
        setPortalRoute('project')

        void insertProject(created).then(async ({ result, created: row }) => {
          if (!result.ok) {
            notify(`Not saved · ${result.error}`)
            return
          }
          if (!row) return
          setProjects((list) =>
            list.map((item) => (item.id === id ? { ...item, id: row.id } : item)),
          )
          setActiveProjectId((current) => (current === id ? row.id : current))
          const assign = await assignProjectShow(row.id, showId)
          if (!assign.ok) notify(`Episode created, but not linked to its show · ${assign.error}`)
          await reloadProjects()
        })
      }

      if (activeProject.showId) {
        insertEpisode(activeProject.showId)
        return
      }

      /* This project has no show yet, so one is created from its own name
         and client, the project is linked to it, and the new episode is
         added underneath — turning a single project into a show in one
         action, exactly as it looks from the client's side. */
      const showSlug = uniqueSlug(
        activeProject.client,
        activeProject.name,
        shows.map((item) => item.slug),
      )
      void insertShow({
        client: activeProject.client,
        name: activeProject.name,
        slug: showSlug,
        /* Whatever this project already had for type/description/contact
           becomes the show's own copy — carried forward rather than
           silently dropped the moment it becomes a show. */
        type: activeProject.type,
        description: activeProject.description,
        clientContact: activeProject.clientContact,
      }).then(({ result, created: show }) => {
        if (!result.ok || !show) {
          notify(`Not saved · ${result.error ?? 'could not create the show'}`)
          return
        }
        patchProject(projectId, { showId: show.id })
        void assignProjectShow(projectId, show.id)
        void reloadShows()
        insertEpisode(show.id)
      })
    },

    openShow: (id) => {
      setActiveAdminShowId(id)
      setPortalRoute('show')
    },
    activeAdminShow,
    adminShowProjects: activeAdminShowId
      ? projects.filter((item) => item.showId === activeAdminShowId).sort(byEpisodeName)
      : [],
    addEpisodeToShow: (showId, name) => {
      const episodeName = name.trim()
      if (!episodeName) return
      const show = shows.find((item) => item.id === showId)
      if (!show) return

      const id = uid('proj')
      const created: Project = {
        id,
        slug: uniqueSlug(show.client, episodeName, projects.map((item) => item.slug)),
        /* Client, type, contact and description all come from the show —
           they're shared across every episode, so a new one starts already
           in step rather than blank and waiting for Edit Show to fix it. */
        client: show.client,
        name: episodeName,
        type: show.type,
        description: show.description,
        status: 'Planned',
        nextShootDate: '',
        nextShootTime: '',
        location: '',
        clientContact: show.clientContact,
        producer: 'Portal Productions',
        updated: stamp(),
        deliverables: [],
        shoots: [],
        team: [],
        archived: false,
      }
      setProjects((list) => [created, ...list])

      void insertProject(created).then(async ({ result, created: row }) => {
        if (!result.ok) {
          notify(`Not saved · ${result.error}`)
          return
        }
        if (!row) return
        setProjects((list) =>
          list.map((item) => (item.id === id ? { ...item, id: row.id } : item)),
        )
        const assign = await assignProjectShow(row.id, showId)
        if (!assign.ok) notify(`Episode created, but not linked to its show · ${assign.error}`)
        await reloadProjects()
      })
    },

    selectProject: (id) => {
      setActiveProjectId(id)
      setFocusDeliverableId('')
    },

    /*
     * Client name, type, description and client contact describe the show
     * as a whole, not any one episode — so they are edited here, once, and
     * mirrored onto every episode's own copy of the same field (including
     * archived ones, so the archive never shows a client relationship that
     * has since moved on). Takes an explicit showId rather than reading
     * activeAdminShowId, so it works equally from the Show Admin page and
     * from any single episode's Project Settings — including a show with
     * only one episode so far, which has no Show Admin page to visit yet.
     */
    updateShow: (showId, patch) => {
      const show = shows.find((item) => item.id === showId)
      if (!show) return

      setShows((list) => list.map((item) => (item.id === showId ? { ...item, ...patch } : item)))
      void updateShowRow(showId, show.slug, patch).then((result) => {
        if (!result.ok) notify(`Not saved · ${result.error}`)
      })

      const episodeFields: Partial<Project> = {}
      if (patch.client !== undefined) episodeFields.client = patch.client
      if (patch.type !== undefined) episodeFields.type = patch.type
      if (patch.description !== undefined) episodeFields.description = patch.description
      if (patch.clientContact !== undefined) episodeFields.clientContact = patch.clientContact
      if (Object.keys(episodeFields).length === 0) return

      const episodes = projects.filter((item) => item.showId === showId)
      if (episodes.length === 0) return

      setProjects((list) =>
        list.map((item) => (item.showId === showId ? { ...item, ...episodeFields } : item)),
      )
      for (const episode of episodes) {
        void updateProjectRow(episode.id, episode.slug, episodeFields).then((result) => {
          if (!result.ok) notify(`${episode.name}: not saved · ${result.error}`)
        })
      }
    },

    projectComplete: activeProject.archived || projectStatus(activeProject) === 'Complete',
    /* Archiving only changes the project's status — every deliverable,
       change request, approval, activity record and its access token stay
       exactly as they are. Nothing is ever deleted. */
    archiveProject: () => {
      patchProject(projectId, { archived: true, status: 'Delivered' })
      void updateProjectRow(projectId, activeProject.slug, { archived: true }).then((result) => {
        if (!result.ok) notify(`Not archived · ${result.error}`)
      })
    },
    /* Permanent, and only for test, duplicate or mistaken projects. The whole
       deletion happens in one database transaction, so the project is only
       removed from the interface once Supabase confirms it succeeded. */
    deleteProject: async () => {
      const { slug, client, name } = activeProject
      const result = await deleteProjectRow(projectId, slug)
      if (!result.ok) {
        notify(result.error ?? 'Project could not be deleted. Nothing was removed.')
        return false
      }
      const remaining = projects.filter((item) => item.id !== projectId)
      setProjects(remaining)
      setActiveProjectId(remaining[0]?.id ?? '')
      setActiveDeliverableId('')
      setPortalRoute('projects')
      notify(`Project deleted · ${client} — ${name}`)
      return true
    },

    /** An active project with the same client and project name, if one exists. */
    findExistingProject: (client, name) => {
      const key = (value: string) => value.trim().toLowerCase()
      return (
        projects.find(
          (item) =>
            !item.archived && key(item.client) === key(client) && key(item.name) === key(name),
        ) ?? null
      )
    },

    reopenProject: (id) => {
      const slug = projects.find((item) => item.id === id)?.slug ?? ''
      setProjects((list) =>
        list.map((item) =>
          item.id === id
            ? { ...item, archived: false, status: 'In Progress' }
            : item,
        ),
      )
      void updateProjectRow(id, slug, { archived: false }).then((result) => {
        if (!result.ok) notify(`Not restored · ${result.error}`)
      })
    },

    updateProject: (patch) => {
      patchProject(projectId, patch)
      void updateProjectRow(projectId, activeProject.slug, patch).then((result) => {
        if (!result.ok) notify(`Not saved · ${result.error}`)
      })

      const touchesShoot =
        patch.nextShootDate !== undefined ||
        patch.nextShootTime !== undefined ||
        patch.location !== undefined
      if (!touchesShoot) return

      setProjects((list) =>
        list.map((item) => {
          if (item.id !== projectId) return item
          const date = patch.nextShootDate ?? item.nextShootDate
          const time = patch.nextShootTime ?? item.nextShootTime
          const location = patch.location ?? item.location

          if (item.shoots.length === 0) {
            if (!date) return item
            return {
              ...item,
              shoots: [
                {
                  id: uid('shoot'),
                  date,
                  title: 'Shoot Day',
                  time,
                  city: '',
                  location,
                  details: [],
                },
              ],
            }
          }

          return {
            ...item,
            shoots: item.shoots.map((shoot, index) =>
              index === 0 ? { ...shoot, date: date || shoot.date, time, location } : shoot,
            ),
          }
        }),
      )
    },

    createProject: ({ client, name, type, templateId, showId }) => {
      const template = templates.find((item) => item.id === templateId)
      const id = uid('proj')
      const created: Project = {
        id,
        /* The client link is minted once here and never changes. */
        slug: uniqueSlug(
          client,
          name,
          projects.map((item) => item.slug),
        ),
        client,
        name,
        type: type || template?.projectType || 'Production',
        description: '',
        status: 'Planned',
        nextShootDate: '',
        nextShootTime: '',
        location: '',
        clientContact: '',
        producer: 'Portal Productions',
        updated: stamp(),
        deliverables: (template?.deliverables ?? []).map(fromTemplate),
        shoots: [],
        team: [],
        archived: false,
      }
      setProjects((list) => [created, ...list])
      setActiveProjectId(id)
      setPortalRoute('project')

      /* Stored immediately, then re-keyed to the database's own UUID so the
         project and its link are identical in every browser. */
      void insertProject(created).then(async ({ result, created: row }) => {
        if (!result.ok) {
          /* Two staff creating the same project at once: the database refuses
             the duplicate, so the existing project is opened instead. */
          if (result.error?.includes('23505')) {
            setProjects((list) => list.filter((item) => item.id !== id))
            const rows = await fetchProjects()
            const existing = rows.find((item) => item.slug === created.slug)
            await reloadProjects()
            if (existing) {
              setActiveProjectId(existing.id)
              setPortalRoute('project')
              notify('This project already exists · opened it instead')
              return
            }
          }
          notify(`Not saved · ${result.error}`)
          return
        }
        if (!row) return
        setProjects((list) =>
          list.map((item) => (item.id === id ? { ...item, id: row.id } : item)),
        )
        setActiveProjectId((current) => (current === id ? row.id : current))
        /* A show chosen while creating the project is assigned once the real
           row exists — the same order every other post-creation write follows. */
        if (showId) {
          const assign = await assignProjectShow(row.id, showId)
          if (!assign.ok) notify(`Project created, but not linked to its show · ${assign.error}`)
        }
        /* Read the stored rows back so the project page shows what exists. */
        await reloadProjects()
      })
    },

    duplicateProject: () => {
      const id = uid('proj')
      const copy: Project = {
        ...activeProject,
        id,
        slug: uniqueSlug(
          `${activeProject.client} Copy`,
          activeProject.name,
          projects.map((item) => item.slug),
        ),
        client: `${activeProject.client} (Copy)`,
        status: 'Planned',
        updated: stamp(),
        deliverables: activeProject.deliverables.map((item) => ({
          ...item,
          id: uid('d'),
          /** A duplicate is new work, so it starts at the beginning again. */
          status: 'Planned',
          version: '',
          versionDate: '—',
          driveUrl: '',
          audioUrl: '',
          notes: [],
          changeRequests: [],
          versions: [],
        })),
        shoots: [],
        /* Assignments belong to the original project's rows, so a duplicate
           starts unassigned rather than showing team that was never saved. */
        team: [],
        archived: false,
      }
      setProjects((list) => [copy, ...list])
      setActiveProjectId(id)
      setPortalRoute('project')
    },

    saveAsTemplate: (name) => {
      setTemplates((list) => [
        ...list,
        {
          id: uid('tpl'),
          name,
          projectType: activeProject.type,
          deliverables: activeProject.deliverables.map((item) => ({
            name: item.name,
            type: item.type,
            status: 'Planned',
            visibleToClient: item.visibleToClient,
          })),
        },
      ])
    },

    updateShoot: (id, patch) =>
      setProjects((list) =>
        list.map((item) =>
          item.id === projectId
            ? {
                ...item,
                shoots: item.shoots.map((shoot) =>
                  shoot.id === id ? { ...shoot, ...patch } : shoot,
                ),
              }
            : item,
        ),
      ),

    /* project_staff is the source of truth. Replaces whatever the session
       held with the saved assignments whenever the project is opened. */
    setProjectTeam: (team) => patchProject(projectId, { team }),

    /* Nothing is shown until project_staff confirms the row exists. */
    addTeamMember: ({ userId, name, role }) => {
      if (activeProject.team.some((member) => member.id === userId)) return

      void assignProjectMember(activeProject.id, activeProject.slug, userId, role).then(
        (result) => {
          if (!result.ok) {
            notify('Team assignment could not be saved.')
            return
          }
          patchProject(projectId, {
            team: [...activeProject.team, { id: userId, name, role }],
          })
        },
      )
    },

    updateTeamMember: (id, patch) => {
      const previous = activeProject.team
      patchProject(projectId, {
        team: previous.map((member) => (member.id === id ? { ...member, ...patch } : member)),
      })

      if (patch.role === undefined) return
      void updateProjectMemberRole(activeProject.id, activeProject.slug, id, patch.role).then(
        (result) => {
          if (result.ok) return
          patchProject(projectId, { team: previous })
          notify('Team assignment could not be saved.')
        },
      )
    },

    /* Anyone assigned can be removed — current user, owner, creator alike. */
    removeTeamMember: (id) => {
      const previous = activeProject.team
      void removeProjectMember(activeProject.id, activeProject.slug, id).then((result) => {
        if (!result.ok) {
          // They stay on screen, because they are still in the database.
          notify('Team assignment could not be removed.')
          return
        }
        patchProject(projectId, { team: previous.filter((member) => member.id !== id) })
      })
    },
  }
}
